import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, Upload, Loader2, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { ContentEditor } from '@/components/ContentEditor';
import { CoreValuesEditor } from '@/components/CoreValuesEditor';
import { AdminLogin } from '@/components/AdminLogin';
import { ImageUploader } from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminPanelContent onLogout={handleLogout} />;
};

const AdminPanelContent = ({ onLogout }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    area: '',
    address: '',
    map_url: '',
    room_types: [],
    amenities: [],
    images: [],
    video_url: '',
    pricing: { single: '', double: '', triple: '', quadruple: '' },
    description: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties?per_page=100`);
      setProperties(response.data.properties);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch properties');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePricingChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [type]: value
      }
    }));
  };

  const handleArrayInput = (field, value) => {
    const array = value.split('\n').filter(item => item.trim() !== '');
    setFormData(prev => ({
      ...prev,
      [field]: array
    }));
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      area: property.area,
      address: property.address,
      map_url: property.map_url,
      room_types: property.room_types,
      amenities: property.amenities,
      images: property.images,
      video_url: property.video_url || '',
      pricing: property.pricing,
      description: property.description || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/properties/${id}`);
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Clean up pricing - only include non-empty values and convert to numbers
      const cleanPricing = {};
      if (formData.pricing.single && parseInt(formData.pricing.single) > 0) {
        cleanPricing.single = parseInt(formData.pricing.single);
      }
      if (formData.pricing.double && parseInt(formData.pricing.double) > 0) {
        cleanPricing.double = parseInt(formData.pricing.double);
      }
      if (formData.pricing.triple && parseInt(formData.pricing.triple) > 0) {
        cleanPricing.triple = parseInt(formData.pricing.triple);
      }
      if (formData.pricing.quadruple && parseInt(formData.pricing.quadruple) > 0) {
        cleanPricing.quadruple = parseInt(formData.pricing.quadruple);
      }

      // Check if at least one room type is provided
      if (Object.keys(cleanPricing).length === 0) {
        toast.error('Please add at least one room type with pricing');
        setSaving(false);
        return;
      }

      const payload = {
        ...formData,
        pricing: cleanPricing
      };

      if (editingProperty) {
        await axios.put(`${BACKEND_URL}/api/properties/${editingProperty.id}`, payload);
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/properties`, payload);
        toast.success('Property created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error('Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingProperty(null);
    setFormData({
      name: '',
      area: '',
      address: '',
      map_url: '',
      room_types: [],
      amenities: [],
      images: [],
      video_url: '',
      pricing: { single: '', double: '', triple: '', quadruple: '' },
      description: ''
    });
  };

  const handleNewProperty = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600 font-body mt-2">Manage properties and edit website content</p>
          </div>
          <Button
            onClick={onLogout}
            variant="outline"
            className="font-body"
            data-testid="logout-button"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="properties" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="properties" className="font-body text-lg">Properties</TabsTrigger>
            <TabsTrigger value="content" className="font-body text-lg">Content</TabsTrigger>
            <TabsTrigger value="values" className="font-body text-lg">Core Values</TabsTrigger>
          </TabsList>

          <TabsContent value="properties">
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-heading text-2xl font-semibold text-gray-900">Properties</h2>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={handleNewProperty}
                className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
                data-testid="add-property-button"
              >
                <Plus size={18} className="mr-2" />
                Add New Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {editingProperty ? 'Edit Property' : 'Add New Property'}
                </DialogTitle>
                <DialogDescription>
                  Fill in the property details. You can add multiple images and a video URL.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Property Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Indweller Andheri Premium"
                      className="font-body"
                    />
                  </div>

                  <div>
                    <Label htmlFor="area">Area/Location *</Label>
                    <Input
                      id="area"
                      name="area"
                      required
                      value={formData.area}
                      onChange={handleInputChange}
                      placeholder="Andheri East, Mumbai"
                      className="font-body"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Link Road, Andheri East, Mumbai 400069"
                    className="font-body"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the property..."
                    rows={3}
                    className="font-body"
                  />
                </div>

                <div>
                  <Label htmlFor="map_url">Google Maps Embed URL *</Label>
                  <Input
                    id="map_url"
                    name="map_url"
                    required
                    value={formData.map_url}
                    onChange={handleInputChange}
                    placeholder="https://www.google.com/maps/embed?pb=..."
                    className="font-body"
                  />
                  <p className="text-xs text-gray-500 mt-1">Get embed URL from Google Maps → Share → Embed a map</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="single_price">Single Sharing (₹/month)</Label>
                    <Input
                      id="single_price"
                      type="number"
                      value={formData.pricing.single || ''}
                      onChange={(e) => handlePricingChange('single', e.target.value)}
                      placeholder="15000 (leave empty if not available)"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <Label htmlFor="double_price">Double Sharing (₹/month)</Label>
                    <Input
                      id="double_price"
                      type="number"
                      value={formData.pricing.double || ''}
                      onChange={(e) => handlePricingChange('double', e.target.value)}
                      placeholder="10000 (leave empty if not available)"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <Label htmlFor="triple_price">Triple Sharing (₹/month)</Label>
                    <Input
                      id="triple_price"
                      type="number"
                      value={formData.pricing.triple || ''}
                      onChange={(e) => handlePricingChange('triple', e.target.value)}
                      placeholder="8000 (leave empty if not available)"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quadruple_price">Quadruple/4-Sharing (₹/month)</Label>
                    <Input
                      id="quadruple_price"
                      type="number"
                      value={formData.pricing.quadruple || ''}
                      onChange={(e) => handlePricingChange('quadruple', e.target.value)}
                      placeholder="6000 (leave empty if not available)"
                      className="font-body"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 font-body">
                  💡 Tip: Only fill in the room types you offer. Leave others empty if not available.
                </p>

                <div>
                  <Label htmlFor="room_types">Room Types (one per line) *</Label>
                  <Textarea
                    id="room_types"
                    value={formData.room_types.join('\n')}
                    onChange={(e) => handleArrayInput('room_types', e.target.value)}
                    placeholder="Single Sharing&#10;Double Sharing&#10;Triple Sharing"
                    rows={3}
                    className="font-body"
                  />
                </div>

                <div>
                  <Label htmlFor="amenities">Amenities (one per line) *</Label>
                  <Textarea
                    id="amenities"
                    value={formData.amenities.join('\n')}
                    onChange={(e) => handleArrayInput('amenities', e.target.value)}
                    placeholder="High-Speed WiFi&#10;3 Meals Daily&#10;24/7 Security&#10;AC Rooms"
                    rows={5}
                    className="font-body"
                  />
                </div>

                <div>
                  <Label>Property Images *</Label>
                  <ImageUploader
                    images={formData.images}
                    onImagesChange={(newImages) => setFormData(prev => ({ ...prev, images: newImages }))}
                  />
                </div>

                <div>
                  <Label htmlFor="video_url">Video URL (YouTube/Vimeo embed)</Label>
                  <Input
                    id="video_url"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleInputChange}
                    placeholder="https://www.youtube.com/embed/..."
                    className="font-body"
                  />
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="font-body"
                  >
                    <X size={18} className="mr-2" />
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={18} className="mr-2" />
                        {editingProperty ? 'Update Property' : 'Create Property'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin text-brand-purple" size={48} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {properties.map((property) => (
              <motion.div
                key={property.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-heading text-2xl font-semibold text-gray-900 mb-2">
                        {property.name}
                      </h3>
                      <p className="text-gray-600 font-body mb-2">{property.area}</p>
                      <p className="text-sm text-gray-500 font-body mb-4">{property.address}</p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {property.room_types.map((type, idx) => (
                          <span key={idx} className="bg-brand-purple/10 text-brand-purple px-3 py-1 rounded-full text-sm font-body">
                            {type}
                          </span>
                        ))}
                      </div>

                      <div className="grid grid-cols-3 gap-4 mb-4">
                        {Object.entries(property.pricing).map(([type, price]) => {
                          const roomTypeLabels = {
                            single: 'Single',
                            double: 'Double',
                            triple: 'Triple',
                            quadruple: '4-Sharing'
                          };
                          return price > 0 && (
                            <div key={type} className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-sm text-gray-600 font-body">{roomTypeLabels[type] || type}</p>
                              <p className="text-lg font-semibold text-brand-purple font-heading">₹{price}/mo</p>
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex gap-2">
                        <span className="text-sm text-gray-500 font-body">{property.images.length} images</span>
                        <span className="text-sm text-gray-500">•</span>
                        <span className="text-sm text-gray-500 font-body">{property.amenities.length} amenities</span>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleEdit(property)}
                        variant="outline"
                        size="sm"
                        className="font-body"
                        data-testid={`edit-property-${property.id}`}
                      >
                        <Edit size={16} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(property.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 font-body"
                        data-testid={`delete-property-${property.id}`}
                      >
                        <Trash2 size={16} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </TabsContent>

      <TabsContent value="content">
        <ContentEditor />
      </TabsContent>

      <TabsContent value="values">
        <CoreValuesEditor />
      </TabsContent>
    </Tabs>
      </div>
    </div>
  );
};
